import React, { useState, useEffect } from "react";
import { RefreshCw, Play } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from "../ui/card";
import { getRepositoryPipelineFromYAML } from "../../api/repositoryApi";

/**
 * Component for triggering deployment pipelines
 */
const DeploymentTrigger = ({
  selectedPipelineConfig,
  setSelectedPipelineConfig,
  isTriggeringPipeline,
  onTriggerPipeline,
  toast,
  repositories,
  selectedRepository,
  workspace,
}) => {
  const [deploymentPipelines, setDeploymentPipelines] = useState([]);
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);

  // Fetch pipelines from YAML when repository changes
  useEffect(() => {
    const fetchPipelines = async () => {
      if (selectedRepository && workspace) {
        try {
          setIsLoadingPipelines(true);
          const pipelineData = await getRepositoryPipelineFromYAML(
            workspace,
            selectedRepository
          );

          // Extract branch pipelines that contain "Deploy to" in their names
          const deploymentBranches = Object.keys(pipelineData.branches || {})
            .filter((pattern) => pattern.toLowerCase().includes("deploy to"))
            .map((pattern) => ({
              selector: {
                type: "branches",
                pattern: pattern,
              },
              displayName: pattern,
            }));

          setDeploymentPipelines(deploymentBranches);

          // Auto-select first deployment pipeline if available
          if (deploymentBranches.length > 0 && !selectedPipelineConfig) {
            const pipelineValue = JSON.stringify({
              selector: {
                type: "branches",
                pattern: deploymentBranches[0].selector.pattern,
              },
            });
            setSelectedPipelineConfig(pipelineValue);
          }

          setIsLoadingPipelines(false);
        } catch (error) {
          console.error("Error fetching pipeline data:", error);
          setIsLoadingPipelines(false);
          if (toast) {
            toast({
              title: "Error",
              description: "Failed to load deployment pipelines",
              variant: "destructive",
            });
          }
        }
      }
    };

    fetchPipelines();
  }, [
    selectedRepository,
    workspace,
    setSelectedPipelineConfig,
    selectedPipelineConfig,
    toast,
  ]);

  return (
    <>
      <CardHeader>
        <CardTitle>Trigger Deployment</CardTitle>
        <CardDescription>
          Run deployment pipelines for the selected repository
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <Label htmlFor="pipeline-config" className="mb-2 block">
              Deployment Pipeline
            </Label>
            <Select
              value={selectedPipelineConfig}
              onValueChange={setSelectedPipelineConfig}
            >
              <SelectTrigger id="pipeline-config" className="w-full">
                <SelectValue
                  placeholder={
                    isLoadingPipelines
                      ? "Loading pipelines..."
                      : "Select Deployment Pipeline"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {deploymentPipelines.map((pipeline, index) => {
                  const pipelineValue = JSON.stringify({
                    selector: {
                      type: "branches",
                      pattern: pipeline.selector.pattern,
                    },
                  });

                  return (
                    <SelectItem key={index} value={pipelineValue}>
                      {pipeline.displayName || pipeline.selector.pattern}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => {
              // Parse the selected pipeline config
              if (!selectedPipelineConfig) {
                toast({
                  title: "Error",
                  description: "Please select a pipeline configuration",
                  variant: "destructive",
                });
                return;
              }

              // Get default branch
              const repo = repositories.find(
                (r) => r.slug === selectedRepository
              );
              const branch = repo?.mainbranch?.name || "master";

              // Get pattern from the selected pipeline config
              const config = JSON.parse(selectedPipelineConfig);
              const pattern = config.selector?.pattern;

              onTriggerPipeline(selectedRepository, branch, pattern);
            }}
            disabled={
              isTriggeringPipeline ||
              !selectedPipelineConfig ||
              isLoadingPipelines
            }
            className="mb-1 min-w-[150px]"
          >
            {isTriggeringPipeline ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Deploy Now
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </>
  );
};

export default DeploymentTrigger;
